import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, Pause, Rewind, FastForward, ArrowLeft, Share2, X } from 'lucide-react';
import * as Sentry from '@sentry/react';
import analytics from '../lib/tracking';
import { API_BASE_URL } from '../config/api';
import { findSessionById, type Session } from '../data/sessions';
import { useSessions } from '../hooks/useSessions';
import { useAuth } from '../hooks/useAuth';
import { endAudioReadyJourney, tracer, withSpan } from '../lib/telemetry';

export default function SessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { sessions: catalog, loading: catalogLoading } = useSessions();
  const { getValidAuthHeaders, refreshTokens } = useAuth();

  const [session, setSession] = useState<(Session & { category?: string }) | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showCopiedFeedback, setShowCopiedFeedback] = useState(false);
  const [isMounted, setIsMounted] = useState(true);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [voteCount, setVoteCount] = useState(0);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Find session by ID across all categories. Wait for the catalog to
  // resolve before deciding "not found" — otherwise an in-flight fetch
  // would bounce the user back to home.
  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    if (catalogLoading || Object.keys(catalog).length === 0) return;

    const result = findSessionById(catalog, sessionId);

    if (!result) {
      const span = tracer.startSpan('session.invalid_access', {
        attributes: {
          'session.id': sessionId,
          'session.referrer': document.referrer || 'direct',
        },
      });
      span.end();
      console.error('Session not found:', sessionId);
      analytics.invalidSession(sessionId, {
        attempted_from: document.referrer
      });
      navigate('/');
      return;
    }

    const sessionWithCategory = { ...result.session, category: result.category };
    setSession(sessionWithCategory);

    // Track page view
    analytics.pageView(`/session/${sessionId}`, result.session.title, {
      session_category: result.category,
      has_audio: !!result.session.audioFile
    });
  }, [sessionId, navigate, catalog, catalogLoading]);

  // Track component mount status
  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  // Audio management
  useEffect(() => {
    if (!session?.audioFile || audioElement) return;

    let audio: HTMLAudioElement | null = null;
    let isCleaningUp = false;

    // Open span when we have a URL + Audio element; closed when the
    // browser reports loadedmetadata (player UI swaps in). Stored in a
    // closure so the event handler can reach it.
    let loadSpan: ReturnType<typeof tracer.startSpan> | null = null;

    const handleLoadedMetadata = () => {
      if (isMounted && audio) {
        setDuration(audio.duration);
        setIsLoadingAudio(false); // Audio is ready
      }
      if (loadSpan) {
        loadSpan.setAttribute('audio.duration', audio?.duration ?? -1);
        loadSpan.end();
        loadSpan = null;
      }
      // End the cross-component journey span opened by HomePage's click.
      endAudioReadyJourney(session.id, 'ready', {
        'audio.duration': audio?.duration ?? -1,
      });
    };

    const handleTimeUpdate = () => {
      if (isMounted && audio) {
        setCurrentTime(audio.currentTime);
      }
    };

    const handleEnded = () => {
      if (isMounted) {
        setIsPlaying(false);
        setCurrentTime(0);
      }
    };

    const handleError = (e: Event) => {
      // Only handle errors if component is still mounted, not cleaning up, and audio has a valid source
      if (!isMounted || isCleaningUp || !audio?.src) return;

      const target = e.target as HTMLAudioElement;
      console.error('Audio error:', e);

      const errorCode = target.error?.code || 'unknown';
      const errorMessage = target.error?.message || 'Audio playback failed';

      const errorContext = {
        audio_file: session.audioFile,
        session_title: session.title,
        error_code: errorCode,
        user_agent: navigator.userAgent,
        audio_url: target.src || 'unknown'
      };

      analytics.error('audio_playback_error', errorMessage, errorContext);
      endAudioReadyJourney(
        session.id,
        'error',
        {
          'audio.error_code': String(errorCode),
          'audio.error_stage': 'playback',
        },
        new Error(`Audio playback error: ${errorMessage}`)
      );

      Sentry.captureException(new Error(`Audio playback error: ${errorMessage}`), {
        level: 'error',
        tags: {
          error_type: 'audio_playback_error',
          error_code: errorCode,
          audio_file: session.audioFile
        },
        extra: errorContext
      });

      alert(`Audio failed to load: ${session.title}\n\nError: ${errorMessage}`);
    };

    const fetchAudioUrl = async () => {
      try {
        setIsLoadingAudio(true);
        const data = await withSpan(
          'audio.fetch_url',
          {
            'session.id': session.id,
            'audio.file': session.audioFile,
          },
          async () => {
            const audioUrl = new URL('audio/get-url', API_BASE_URL);
            const body = JSON.stringify({
              sessionId: session.id,
              contentId: session.audioFile,
            });

            const doFetch = async () =>
              fetch(audioUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  // Proactively refreshes an expiring token before the request.
                  ...(await getValidAuthHeaders()),
                },
                body,
              });

            let response = await doFetch();

            // Belt-and-braces: if the token expired between the proactive
            // check and the request landing, refresh once and replay.
            if (response.status === 401) {
              await refreshTokens();
              response = await doFetch();
            }

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              throw new Error(
                `HTTP ${response.status}: ${errorData.error || 'Failed to get audio URL'}`
              );
            }

            return await response.json();
          }
        );

        audio = new Audio(data.url);

        // Track time from "we have a URL" to "browser is ready to play".
        loadSpan = tracer.startSpan('audio.load', {
          attributes: {
            'session.id': session.id,
            'audio.file': session.audioFile,
          },
        });

        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('error', handleError);

        setAudioElement(audio);
      } catch (error) {
        if (!isMounted) return;

        setIsLoadingAudio(false);
        console.error('Failed to get audio URL:', error);

        const errorContext = {
          audio_file: session.audioFile,
          session_title: session.title,
          session_id: session.id,
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          error_stack: error instanceof Error ? error.stack : undefined
        };

        analytics.error('audio_url_error', error instanceof Error ? error.message : 'Unknown error', errorContext);
        endAudioReadyJourney(
          session.id,
          'error',
          { 'audio.error_stage': 'fetch_url' },
          error instanceof Error ? error : new Error(String(error))
        );

        Sentry.captureException(error, {
          level: 'error',
          tags: {
            error_type: 'audio_url_error',
            audio_file: session.audioFile
          },
          extra: errorContext
        });

        alert(`Failed to load audio: ${session.title}\n\n${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    };

    fetchAudioUrl();

    // Cleanup on unmount
    return () => {
      isCleaningUp = true;
      if (loadSpan) {
        // Component unmounted before the audio was ready — record so the
        // span tree is consistent.
        loadSpan.setAttribute('audio.load.aborted', true);
        loadSpan.end();
        loadSpan = null;
      }
      // If the user navigated away before loadedmetadata/error fired, the
      // journey is over — abort it so the span doesn't leak in the Map.
      if (session?.id) {
        endAudioReadyJourney(session.id, 'aborted');
      }
      if (audio) {
        // Remove event listeners before cleanup to prevent error events
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('error', handleError);

        audio.pause();
        audio.src = '';
        audio.load(); // Reset the audio element
      }
    };
  }, [session, isMounted]);

  const handlePlayPause = () => {
    if (!audioElement) return;

    if (isPlaying) {
      audioElement.pause();
      setIsPlaying(false);
    } else {
      const playSpan = tracer.startSpan('audio.play', {
        attributes: {
          'session.id': session?.id ?? 'unknown',
          'audio.file': session?.audioFile ?? 'unknown',
          'audio.position': audioElement.currentTime,
        },
      });
      audioElement.play().then(() => {
        playSpan.setAttribute('audio.play.started', true);
        playSpan.end();
        setIsPlaying(true);
        // Track play event on manual play
        analytics.engagement('play_session', 'audio', {
          session_id: session?.id,
          session_title: session?.title,
          session_category: session?.category
        });
      }).catch(e => {
        playSpan.recordException(e as Error);
        playSpan.setAttribute('audio.play.started', false);
        playSpan.end();
        console.error('Playback failed:', e);
        analytics.error('playback_error', 'Audio playback failed');
      });
    }
  };

  const handleSkipBackward = () => {
    if (!audioElement) return;
    audioElement.currentTime = Math.max(0, audioElement.currentTime - 10);
    analytics.engagement('skip_backward', 'audio', {
      session_id: session?.id,
      position: audioElement.currentTime
    });
  };

  const handleSkipForward = () => {
    if (!audioElement) return;
    audioElement.currentTime = Math.min(duration, audioElement.currentTime + 10);
    analytics.engagement('skip_forward', 'audio', {
      session_id: session?.id,
      position: audioElement.currentTime
    });
  };

  const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioElement || !duration) return;

    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;

    audioElement.currentTime = Math.max(0, Math.min(duration, newTime));

    analytics.engagement('scrub_audio', 'audio', {
      session_id: session?.id,
      from_position: currentTime,
      to_position: newTime,
      percentage: percentage * 100
    });
  };

  const handleBack = () => {
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
    }
    setIsPlaying(false);
    navigate('/');
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/session/${sessionId}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setShowCopiedFeedback(true);
      setTimeout(() => setShowCopiedFeedback(false), 2000);

      analytics.engagement('share_session', 'social', {
        session_id: sessionId,
        session_title: session?.title,
        method: 'clipboard'
      });
    } catch (err) {
      console.error('Failed to copy:', err);
      alert(`Copy this link to share:\n${shareUrl}`);
    }
  };

  const handleVote = async () => {
    if (hasVoted) return;

    try {
      // Track vote engagement event (sends to backend API automatically)
      analytics.engagement('vote_session', 'interaction', {
        session_id: sessionId,
        session_title: session?.title,
        session_category: session?.category
      });

      setHasVoted(true);
      setVoteCount(prev => prev + 1);
    } catch (error) {
      console.error('Failed to vote:', error);

      const errorContext = {
        session_id: sessionId,
        session_title: session?.title,
        error_message: error instanceof Error ? error.message : 'Unknown error'
      };

      analytics.error('vote_error', error instanceof Error ? error.message : 'Unknown error', errorContext);

      Sentry.captureException(error, {
        level: 'error',
        tags: {
          error_type: 'vote_error',
          session_id: sessionId
        },
        extra: errorContext
      });

      alert('Failed to record your vote. Please try again.');
    }
  };

  const handleOpenFeedbackModal = () => {
    analytics.engagement('open_feedback_modal', 'modal', {
      session_id: sessionId,
      session_title: session?.title,
      session_category: session?.category,
      has_audio: !!session?.audioFile
    });
    setShowFeedbackModal(true);
  };

  const formatTime = (time: number): string => {
    if (!time || isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (!session) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">⏳</div>
          <p className="text-text-medium">Loading session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-purple-50">
      {/* Header */}
      <header className="bg-white border-b border-accent-pink sticky top-0 z-50">
        <div className="max-w-lg mx-auto px-5 py-4 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-text-dark hover:text-primary-pink transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </button>

          <button
            onClick={handleShare}
            className="flex items-center gap-2 text-text-dark hover:text-primary-pink transition-colors relative"
          >
            <Share2 className="w-5 h-5" />
            <span className="font-medium">Share</span>

            {showCopiedFeedback && (
              <div className="absolute -bottom-8 right-0 bg-primary-pink text-white text-xs px-3 py-1 rounded-full">
                Copied!
              </div>
            )}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-lg mx-auto px-6 py-12">
        {/* Session Icon */}
        <div className="text-center mb-8">
          <div className="text-8xl mb-6">{session.icon}</div>
          <h1 className="text-3xl font-bold text-text-dark mb-3">{session.title}</h1>
          <p className="text-text-medium leading-relaxed">{session.description}</p>
        </div>

        {/* Audio Player */}
        {session.audioFile ? (
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-accent-pink">
            {isLoadingAudio ? (
              /* Loading State */
              <div className="flex flex-col items-center justify-center py-8">
                <div className="w-16 h-16 border-4 border-primary-pink border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-text-medium text-sm">Loading audio...</p>
              </div>
            ) : (
              <>
                {/* Progress Bar */}
                <div className="mb-6">
                  <div
                    className="h-2 bg-gray-200 rounded-full overflow-hidden cursor-pointer hover:h-3 transition-all"
                    onClick={handleProgressBarClick}
                    role="slider"
                    aria-label="Audio progress"
                    aria-valuemin={0}
                    aria-valuemax={duration}
                    aria-valuenow={currentTime}
                  >
                    <div
                      className="h-full bg-gradient-to-r from-primary-pink to-pastel-purple transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-2 text-sm text-text-medium">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>

                {/* Controls */}
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={handleSkipBackward}
                    disabled={!audioElement}
                    className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center transition-transform active:scale-95 hover:scale-105 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Skip back 10 seconds"
                  >
                    <Rewind className="w-6 h-6 text-text-dark" />
                  </button>

                  <button
                    onClick={handlePlayPause}
                    disabled={!audioElement}
                    className="w-20 h-20 bg-gradient-to-br from-primary-pink to-pastel-purple rounded-full flex items-center justify-center transition-transform active:scale-95 hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? (
                      <Pause className="w-8 h-8 text-white" fill="white" />
                    ) : (
                      <Play className="w-8 h-8 text-white ml-1" fill="white" />
                    )}
                  </button>

                  <button
                    onClick={handleSkipForward}
                    disabled={!audioElement}
                    className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center transition-transform active:scale-95 hover:scale-105 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Skip forward 10 seconds"
                  >
                    <FastForward className="w-6 h-6 text-text-dark" />
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-accent-pink text-center" data-testid="coming-soon-section">
            <p className="text-text-medium mb-4" data-testid="coming-soon-message">Daddy hasn't made this one yet</p>
            <p className="text-sm text-text-medium mb-6" data-testid="vote-prompt">Would you like Daddy to make this?</p>

            <button
              onClick={handleVote}
              disabled={hasVoted}
              data-testid="vote-button"
              className={`px-8 py-3 rounded-full font-medium transition-all ${
                hasVoted
                  ? 'bg-gray-200 text-text-medium cursor-not-allowed'
                  : 'bg-gradient-to-br from-primary-pink to-pastel-purple text-white hover:scale-105 active:scale-95 shadow-lg'
              }`}
            >
              {hasVoted ? '✓ Voted!' : 'Yes, please Daddy!'}
            </button>

            {voteCount > 0 && (
              <p className="text-sm text-text-medium mt-4" data-testid="vote-thank-you">
                Thank you for voting! Your feedback helps us prioritize new content.
              </p>
            )}
          </div>
        )}

        {/* Feedback Button */}
        <div className="mt-6 flex justify-center">
          <button
            onClick={handleOpenFeedbackModal}
            className="w-full max-w-md bg-gradient-to-br from-primary-pink to-pastel-purple text-white font-medium py-4 px-8 rounded-xl transition-transform active:scale-95 hover:scale-105 shadow-lg"
          >
            {session.audioFile ? 'Give Feedback' : 'Share Ideas'}
          </button>
        </div>
      </main>

      {/* Feedback Modal */}
      {showFeedbackModal && session && (
        <FeedbackModal
          onClose={() => setShowFeedbackModal(false)}
          session={session}
        />
      )}
    </div>
  );
}

// Feedback Modal Component
function FeedbackModal({ onClose, session }: { onClose: () => void; session: Session & { category?: string } }) {
  const [feedback, setFeedback] = useState('');
  const [hasStartedTyping, setHasStartedTyping] = useState(false);

  const hasAudio = !!session.audioFile;
  const modalTitle = hasAudio ? 'Give Feedback' : 'Share Ideas';
  const modalDescription = hasAudio
    ? `Share your thoughts about '${session.title}'. What did you like? What could be improved?`
    : `What would you like to see in '${session.title}'? Share your ideas!`;
  const placeholder = hasAudio
    ? 'Your feedback helps us create better experiences...'
    : 'Your ideas help us create experiences you\'ll love...';

  const handleSubmit = () => {
    // Track form submission with type-safe API
    analytics.forms.session_feedback.submit({
      form_text: feedback,
      session_id: session.id,
      session_title: session.title,
      session_category: session.category,
      has_audio: hasAudio,
      ...(session.audioFile && { audio_file: session.audioFile }),
    });

    alert('Thank you! Your feedback has been received.');
    setFeedback('');
    onClose();
  };

  const handleTextChange = (value: string) => {
    setFeedback(value);

    if (!hasStartedTyping && value.length > 0) {
      // Track form start with type-safe API
      analytics.forms.session_feedback.start({
        session_id: session.id
      });
      setHasStartedTyping(true);
    }
  };

  const handleClose = () => {
    // Track form abandon with type-safe API
    analytics.forms.session_feedback.abandon({
      session_id: session.id
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-auto border-2 border-accent-pink">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-text-dark text-xl font-semibold">{modalTitle}</h2>
          <button
            onClick={handleClose}
            aria-label="Close feedback modal"
            className="w-8 h-8 bg-accent-pink rounded-full flex items-center justify-center transition-transform active:scale-95"
          >
            <X className="w-4 h-4 text-text-dark" />
          </button>
        </div>

        {/* Form */}
        <div className="mb-6">
          <p className="text-text-medium text-sm leading-relaxed mb-4">
            {modalDescription}
          </p>

          <textarea
            value={feedback}
            onChange={(e) => handleTextChange(e.target.value)}
            placeholder={placeholder}
            className="w-full bg-pastel-purple bg-opacity-30 text-text-dark rounded-lg p-4 h-32 resize-none focus:outline-none focus:ring-2 focus:ring-primary-pink border border-accent-pink"
          />
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={!feedback.trim()}
          className={`w-full font-medium py-4 px-6 rounded-xl flex items-center justify-center gap-2 transition-colors ${
            feedback.trim()
              ? 'bg-primary-pink hover:bg-pink-400 text-white'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          Send Feedback
        </button>
      </div>
    </div>
  );
}
